import { useState, useEffect } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SignInForm } from "@/components/auth/SignInForm";
import { SignUpForm } from "@/components/auth/SignUpForm";
import { PhoneSignInForm } from "@/components/auth/PhoneSignInForm";
import { PhoneSignUpForm } from "@/components/auth/PhoneSignUpForm";
import { useAuthRedirect } from "@/hooks/useAuthRedirect";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Key } from "lucide-react";
import { Label } from "@/components/ui/label";
import { useTranslation } from "react-i18next";

const Auth = () => {
  const [searchParams] = useSearchParams();
  const { redirectLoggedInUser, isCheckingAuth } = useAuthRedirect();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  
  // Get the default tab from URL parameters or default to "signin"
  const defaultTab = searchParams.get("tab") || "signin";
  const [activeTab, setActiveTab] = useState(defaultTab);
  // Fixed to "email" only, phone authentication is hidden
  const [authMethod] = useState<"email">("email");

  // Password reset states
  const [showResetForm, setShowResetForm] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  // Check if we're in development mode to show debug
  // const isDevMode = process.env.NODE_ENV === 'development';
  // const [showDebug, setShowDebug] = useState(false);

  // Check if user has a password reset token in the URL
  useEffect(() => {
    // Check if user has an oauth error in local storage
    const oauthError = localStorage.getItem("oauthError");
    if(oauthError) {
      toast({
        variant: "destructive",
        title: t('auth.authError'),
        description: oauthError,
      });
      localStorage.removeItem("oauthError");
    }

    const resetPasswordForToken = async () => {
      const hash = window.location.hash;
      const accessToken = searchParams.get("access_token");
      const type = searchParams.get("type");
      
      if (type === "recovery" || (hash && hash.includes("type=recovery"))) {
        setShowResetForm(true);
      } else if (type === "signup" || type === "magiclink" || type === "invite") {
        // Handle other auth callbacks if needed in the future
        console.log("Auth callback type:", type);
      }
    };
    
    resetPasswordForToken();
  }, [searchParams, t, toast]);

  const handleResetPassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({
        variant: "destructive",
        title: t('auth.passwordsDoNotMatch'),
        description: t('auth.passwordsMatchError'),
      });
      return;
    }

    if (newPassword.length < 6) {
      toast({
        variant: "destructive",
        title: t('auth.passwordTooShort'),
        description: t('auth.passwordMinLength'),
      });
      return;
    }

    setIsResetting(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: newPassword,
      });

      if (error) {
        throw error;
      }

      setResetSuccess(true);
      toast({
        title: t('auth.passwordUpdatedSuccessfully'),
        description: t('auth.passwordResetComplete'),
      });
    } catch (error: any) {
      console.error("Error resetting password:", error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: error.message || t('auth.failedToResetPassword'),
      });
    } finally {
      setIsResetting(false);
    }
  };

  const closeResetDialog = () => {
    setShowResetForm(false);
    setResetSuccess(false);
    setNewPassword("");
    setConfirmPassword("");
    
    // Clean up URL
    const cleanParams = new URLSearchParams(searchParams);
    cleanParams.delete("access_token");
    cleanParams.delete("type");
    navigate(`/auth?${cleanParams.toString()}`, { replace: true });
  };

  // Don't render the form while checking auth to prevent flicker
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-pink-50 to-white">
        <div className="flex justify-center mb-8">
          <img 
            src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
            alt="Gebeya Logo" 
            className="h-16 w-16 gebeya-spinner" 
          />
        </div>
        <p className="text-gebeya-pink text-sm animate-pulse">{t('auth.loading')}</p>
      </div>
    );
  }

  const renderAuthContent = () => {
    if (activeTab === "signin") {
      return <SignInForm redirectLoggedInUser={redirectLoggedInUser} />;
    } else {
      return <SignUpForm />;
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-pink-50 to-white p-4">
      <img 
        src="/lovable-uploads/bc4b57d4-e29b-4e44-8e1c-82ec09ca6fd6.png" 
        alt="Gebeya" 
        className="h-12 mb-8 animate-fade-up" 
      />
      
      {/* Debug mode toggle removed */}
      
      <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-xl shadow-lg animate-fade-up [animation-delay:200ms]">
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold tracking-tight bg-gradient-to-r from-gebeya-pink to-gebeya-orange text-transparent bg-clip-text">
            {activeTab === "signup" ? t('auth.startYourJourney') : t('auth.welcomeBack')}
          </h2>
          <p className="text-muted-foreground">
            {activeTab === "signup" 
              ? t('auth.createAccountDescription')
              : t('auth.signInToAccount')
            }
          </p>
        </div>

        <Tabs 
          defaultValue={defaultTab} 
          className="w-full"
          onValueChange={(value) => setActiveTab(value)}
        >
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="signin" className="text-base">{t('auth.signIn')}</TabsTrigger>
            <TabsTrigger value="signup" className="text-base">{t('auth.signUp')}</TabsTrigger>
          </TabsList>

          {/* Phone authentication selector removed */}

          <TabsContent value="signin">
            {renderAuthContent()}
          </TabsContent>

          <TabsContent value="signup">
            {renderAuthContent()}
          </TabsContent>
        </Tabs>
      </div>

      {/* Password Reset Dialog */}
      <AlertDialog open={showResetForm} onOpenChange={closeResetDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl">
              {resetSuccess ? t('auth.passwordResetSuccessful') : t('auth.resetYourPassword')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {resetSuccess 
                ? t('auth.passwordResetSuccessfully')
                : t('auth.enterNewPassword')
              }
            </AlertDialogDescription>
          </AlertDialogHeader>

          {!resetSuccess ? (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('auth.newPassword')}</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="••••••••"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('auth.confirmPassword')}</Label>
                <div className="relative">
                  <Key className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              <AlertDialogFooter className="gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={closeResetDialog}
                  disabled={isResetting}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  onClick={handleResetPassword}
                  className="bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
                  disabled={isResetting}
                >
                  {isResetting ? (
                    <div className="flex items-center gap-2">
                      <img 
                        src="/lovable-uploads/f8714beb-a432-4da6-acab-3ef6f0a42367.png" 
                        alt={t('common.loading')} 
                        className="h-4 w-4 gebeya-spinner" 
                      />
                      <span>{t('auth.resetting')}</span>
                    </div>
                  ) : t('auth.resetPasswordButton')}
                </Button>
              </AlertDialogFooter>
            </div>
          ) : (
            <AlertDialogFooter>
              <AlertDialogAction
                className="w-full bg-gradient-to-r from-gebeya-pink to-gebeya-orange hover:opacity-90"
                onClick={() => {
                  closeResetDialog();
                  setActiveTab("signin");
                }}
              >
                {t('auth.goToSignIn')}
              </AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Auth;
